/**
 * Smart Cell Parser for handling hierarchical Excel data structures
 * Transforms parent-child relationships in Excel sheets into structured columns
 */

export interface HierarchicalParseOptions {
  enableHierarchicalGrouping: boolean; // detect parent-child relationships
  parentColumnSuffix: string; // e.g., ""
  childColumnSuffix: string; // e.g., " sub col a"
}

const DEFAULT_OPTIONS: HierarchicalParseOptions = {
  enableHierarchicalGrouping: true,
  parentColumnSuffix: '',
  childColumnSuffix: ' sub col a',
};

/**
 * Analyzes data to detect the maximum hierarchy depth
 * @param data The data array to analyze
 * @returns Maximum depth found in the hierarchy
 */
function detectMaxHierarchyDepth(data: unknown[][]): number {
  const [, ...dataRows] = data; // Skip header
  let maxDepth = 1;
  let currentHierarchyDepth = 0; // Track how deep we are in the hierarchy

  for (let i = 0; i < dataRows.length; i++) {
    const row = dataRows[i];
    const firstCol = String(row[0] || '').trim();

    if (!firstCol) continue;

    // Check if this row has numerical data
    const hasNumericalData = row.slice(1).some((cell) => {
      if (typeof cell === 'number') return true;
      const str = String(cell || '').trim();
      return str !== '' && !isNaN(Number(str.replace(/[$,%]/g, '')));
    });

    // Count data cells
    const totalDataCells = row.slice(1).filter((cell) => {
      return cell !== undefined && cell !== null && cell !== '';
    }).length;

    const nonEmptyDataCells = row.slice(1).filter((cell) => {
      const str = String(cell || '').trim();
      return str !== '' && str !== '0';
    }).length;

    const totalDataColumns = row.slice(1).length;
    const isSparseTitleRow =
      nonEmptyDataCells <= 2 && totalDataColumns > 5 && totalDataCells <= 2;
    const hasSignificantData = totalDataCells >= 3;

    const isHierarchyRow =
      (!hasNumericalData || isSparseTitleRow) &&
      !hasSignificantData &&
      firstCol;
    const isDataRow = hasNumericalData && !isSparseTitleRow && firstCol;

    if (isHierarchyRow) {
      // Determine if this is a top-level division or nested item
      const isMajorDivision = totalDataCells <= 1;

      if (isMajorDivision) {
        // Major division - reset to level 1
        currentHierarchyDepth = 1;
      } else {
        // Nested item - increment depth
        currentHierarchyDepth += 1;
      }

      // Track the maximum hierarchy depth (not including data level)
      maxDepth = Math.max(maxDepth, currentHierarchyDepth);
    } else if (isDataRow) {
      // Data row - it sits at currentHierarchyDepth + 1
      const dataDepth = currentHierarchyDepth + 1;
      maxDepth = Math.max(maxDepth, dataDepth);
    }
  }

  return Math.max(maxDepth, 2); // Ensure minimum of 2 levels (parent + child)
}

/**
 * Generates dynamic column names for hierarchical data
 * @param baseColumnName The original column name
 * @param numLevels Number of hierarchy levels needed
 * @returns Array of column names like ['Original', 'Original sub col a', 'Original sub col b']
 */
function generateColumnNames(
  baseColumnName: string,
  numLevels: number,
): string[] {
  const columns = [baseColumnName];
  const letters = 'abcdefghijklmnopqrstuvwxyz';
  for (let i = 1; i < numLevels; i++) {
    const letter =
      letters[i - 1] ||
      `${Math.floor((i - 1) / 26) + 1}${letters[(i - 1) % 26]}`;
    columns.push(`${baseColumnName} sub col ${letter}`);
  }
  return columns;
}

/**
 * Analyzes data for hierarchical grouping patterns (parent-child relationships)
 */
export function analyzeHierarchicalGrouping(
  data: unknown[][],
  options: Partial<HierarchicalParseOptions> = {},
): {
  hasHierarchy: boolean;
  parentChildPairs: Array<{
    parentRow: number;
    parentValue: string;
    childRows: Array<{ row: number; value: string }>;
  }>;
  suggestedStructure: {
    parentColumnName: string;
    childColumnName: string;
  } | null;
} {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  if (!opts.enableHierarchicalGrouping || !data.length) {
    return {
      hasHierarchy: false,
      parentChildPairs: [],
      suggestedStructure: null,
    };
  }

  const [, ...dataRows] = data; // Skip header row
  const parentChildPairs: Array<{
    parentRow: number;
    parentValue: string;
    childRows: Array<{ row: number; value: string }>;
  }> = [];

  let currentParent: { row: number; value: string } | null = null;
  let currentChildren: Array<{ row: number; value: string }> = [];

  // Pattern recognition for hierarchical data
  for (let i = 0; i < dataRows.length; i++) {
    const firstCol = String(dataRows[i]?.[0] || '').trim();
    const secondCol = String(dataRows[i]?.[1] || '').trim();

    if (!firstCol) continue;

    // Check if this looks like a parent row (has data in first column but not much data in subsequent columns)
    const isLikelyParent = firstCol && (!secondCol || secondCol === '');

    // Check if this looks like a child row (has data in first column and also data in subsequent columns)
    const isLikelyChild = firstCol && secondCol;

    if (isLikelyParent) {
      // Save previous parent-child group if we had one
      if (currentParent && currentChildren.length > 0) {
        parentChildPairs.push({
          parentRow: currentParent.row,
          parentValue: currentParent.value,
          childRows: [...currentChildren],
        });
      }

      // Start new parent
      currentParent = { row: i + 2, value: firstCol }; // +2 because we skip header and 0-indexed
      currentChildren = [];
    } else if (isLikelyChild && currentParent) {
      // Add to current children
      currentChildren.push({ row: i + 2, value: firstCol });
    }
  }

  // Don't forget the last group
  if (currentParent && currentChildren.length > 0) {
    parentChildPairs.push({
      parentRow: currentParent.row,
      parentValue: currentParent.value,
      childRows: [...currentChildren],
    });
  }

  const hasHierarchy = parentChildPairs.length >= 2; // Need at least 2 parent-child groups

  return {
    hasHierarchy,
    parentChildPairs,
    suggestedStructure: hasHierarchy
      ? {
          parentColumnName: `${String(data[0]?.[0] || 'Row Labels')}${
            opts.parentColumnSuffix
          }`,
          childColumnName: `${String(data[0]?.[0] || 'Row Labels')}${
            opts.childColumnSuffix
          }`,
        }
      : null,
  };
}

/**
 * Applies hierarchical grouping transformation to data
 */
export function applyHierarchicalGrouping(
  data: unknown[][],
  options: Partial<HierarchicalParseOptions> = {},
): unknown[][] {
  const hierarchyAnalysis = analyzeHierarchicalGrouping(data, options);

  if (!hierarchyAnalysis.hasHierarchy) {
    return data;
  }

  const [headerRow, ...dataRows] = data;
  const newHeaderRow = [...headerRow];

  // Dynamic header generation - detect max levels needed
  const originalColumnName = String(headerRow[0] || 'Row Labels');
  const maxDepth = detectMaxHierarchyDepth(data);

  const columnNames = generateColumnNames(originalColumnName, maxDepth);

  // Keep original column as the main column (leftmost)
  newHeaderRow[0] = columnNames[0]; // Original name stays in position 0

  // Add sub-columns to the right
  for (let level = 1; level < maxDepth; level++) {
    newHeaderRow.splice(level, 0, columnNames[level]);
  }

  // Transform data rows with improved classification logic
  const newDataRows: unknown[][] = [];
  let currentLevels: string[] = []; // Dynamic array to track current level at each depth

  for (let i = 0; i < dataRows.length; i++) {
    const row = dataRows[i];
    const firstCol = String(row[0] || '').trim();

    if (!firstCol) {
      // Empty row, keep as is but add empty hierarchy columns for all levels
      const newRow = [...row];
      for (let level = 0; level < maxDepth - 1; level++) {
        newRow.splice(0, 0, '');
      }
      newDataRows.push(newRow);
      continue;
    }

    // Check if this row has numerical data (zeros count as data!)
    const hasNumericalData = row.slice(1).some((cell) => {
      // Handle numeric zeros and other numbers properly
      if (typeof cell === 'number') return true;
      const str = String(cell || '').trim();
      return str !== '' && !isNaN(Number(str.replace(/[$,%]/g, '')));
    });

    // Count non-empty cells but exclude zeros from sparsity check (zeros indicate data, not headers)
    const nonEmptyDataCells = row.slice(1).filter((cell) => {
      const str = String(cell || '').trim();
      return str !== '' && str !== '0';
    }).length;

    // Count total data cells (including zeros) to detect actual data rows
    const totalDataCells = row.slice(1).filter((cell) => {
      // Handle numeric zeros properly
      return cell !== undefined && cell !== null && cell !== '';
    }).length;

    const totalDataColumns = row.slice(1).length;
    // A row is sparse/title-like if it has very few non-zero values AND very few total values
    // But if it has many zeros, it's likely a data row with zero values
    const isSparseTitleRow =
      nonEmptyDataCells <= 2 && totalDataColumns > 5 && totalDataCells <= 2;

    // A row with many zeros is likely data, not a header
    const hasSignificantData = totalDataCells >= 3;

    const isHierarchyCondition =
      (!hasNumericalData || isSparseTitleRow) &&
      !hasSignificantData &&
      firstCol;
    const isDataCondition = hasNumericalData && !isSparseTitleRow && firstCol;

    // Check if this is a column header row that should be preserved
    const isColumnHeaderRow =
      !hasNumericalData &&
      hasSignificantData &&
      firstCol &&
      (firstCol.toLowerCase().includes('labels') || nonEmptyDataCells >= 3);

    if (isColumnHeaderRow) {
      // Preserve column header rows by duplicating static values across hierarchy columns
      const newRow = [...row];
      // Keep original header in position 0, add sub-columns to the right
      for (let level = 1; level < maxDepth; level++) {
        const letters = 'abcdefghijklmnopqrstuvwxyz';
        const letter =
          letters[level - 1] ||
          `${Math.floor((level - 1) / 26) + 1}${letters[(level - 1) % 26]}`;
        newRow.splice(level, 0, `${firstCol} sub col ${letter}`);
      }
      newDataRows.push(newRow);
    } else if (isHierarchyCondition) {
      // Skip column headers - if it has many data columns filled, it's probably a header row
      if (nonEmptyDataCells > 5) {
        continue;
      }

      // Determine level based on spacing/context
      // For simple test data: if it's a hierarchy row with mostly empty data, it's likely a top-level division
      const isMajorDivision = totalDataCells <= 1; // Empty or just one trailing value

      if (isMajorDivision) {
        // Major division - always reset to level 0
        currentLevels = [firstCol];
      } else {
        // Nested item - add to next level
        const newLevel = currentLevels.length;
        currentLevels = [...currentLevels.slice(0, newLevel), firstCol];
      }
      continue;
    } else if (isDataCondition) {
      const newRow = [...row];

      // Add hierarchy levels, filling in empty strings for missing levels
      // Ensure we always have exactly (maxDepth - 1) hierarchy columns
      const hierarchyValues: string[] = [];

      for (let level = 0; level < maxDepth - 1; level++) {
        hierarchyValues.push(currentLevels[level] || '');
      }

      // Insert hierarchy columns: parent levels go in sub-columns (positions 1, 2, etc.)
      // The actual data value stays in position 0 (original column)
      for (let level = 0; level < hierarchyValues.length; level++) {
        newRow.splice(
          level + 1,
          0,
          hierarchyValues[hierarchyValues.length - 1 - level],
        );
      }

      newDataRows.push(newRow);
    }
    // Rows that don't match either condition are skipped
  }

  return [newHeaderRow, ...newDataRows];
}

/**
 * Enhanced Excel data processor that applies hierarchical grouping
 */
export function processExcelDataWithHierarchicalGrouping(
  rawData: unknown[][],
  options: Partial<HierarchicalParseOptions> = {},
): {
  processedData: unknown[][];
  hierarchyReport: {
    applied: boolean;
    parentChildPairs: Array<{
      parentValue: string;
      childCount: number;
    }>;
  };
} {
  // Apply hierarchical grouping
  const hierarchyAnalysis = analyzeHierarchicalGrouping(rawData, options);
  let processedData = rawData;

  if (hierarchyAnalysis.hasHierarchy) {
    processedData = applyHierarchicalGrouping(rawData, options);
  }

  const hierarchyReport = {
    applied: hierarchyAnalysis.hasHierarchy,
    parentChildPairs: hierarchyAnalysis.parentChildPairs.map((pair) => ({
      parentValue: pair.parentValue,
      childCount: pair.childRows.length,
    })),
  };

  return {
    processedData,
    hierarchyReport,
  };
}

/**
 * Utility to detect if data likely contains hierarchical structure that could benefit from grouping
 */
export function detectHierarchicalContent(
  data: unknown[][],
  options: Partial<HierarchicalParseOptions> = {},
): {
  hasHierarchicalContent: boolean;
  confidence: number;
  summary: string;
} {
  const analysis = analyzeHierarchicalGrouping(data, options);

  const confidence = analysis.hasHierarchy
    ? Math.min(100, analysis.parentChildPairs.length * 20)
    : 0; // Higher confidence with more groups

  const summary = analysis.hasHierarchy
    ? `Found ${analysis.parentChildPairs.length} parent-child group(s)`
    : 'No hierarchical structure detected';

  return {
    hasHierarchicalContent: analysis.hasHierarchy,
    confidence: Math.round(confidence),
    summary,
  };
}
